import {
  getBasePayRate,
  getGrandTotal,
  getIncomeItem,
  getMemberRecords,
  getTotalByFieldName,
  getUniqueMemberIds,
} from './incomeTotals.utils';
import {
  INCOME_CHECKLIST_PAY_TYPES,
  PAY_FREQUENCY,
} from 'pages/AffordaWagesAndIncome/constants';

function getCurrentYearStart() {
  const currentYear = new Date().getFullYear();
  return new Date(`${currentYear}-01-01T00:00:00`);
}

function getCurrentYearEnd() {
  const currentYear = new Date().getFullYear();
  return new Date(`${currentYear}-12-31T23:59:59`);
}

function getDateRange(startDate, endDate) {
  const yearStart = getCurrentYearStart();
  const yearEnd = getCurrentYearEnd();

  // If both dates are undefined, use full year
  if (!startDate && !endDate) {
    return {
      start: yearStart,
      end: yearEnd,
    };
  }

  let evalStartDate = startDate ? new Date(startDate) : yearStart;
  let evalEndDate = endDate ? new Date(endDate) : yearEnd;

  // Adjust dates if they're outside current year
  if (evalStartDate < yearStart) {
    evalStartDate = yearStart;
  }
  if (evalEndDate > yearEnd) {
    evalEndDate = yearEnd;
  }

  return {
    start: evalStartDate,
    end: evalEndDate,
  };
}

function calculatePeriods(startDate, endDate, payFrequency) {
  const { start, end } = getDateRange(startDate, endDate);

  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.ceil(diffDays / 7);
  const diffMonths = Math.ceil(diffWeeks / 4.33);

  switch (payFrequency) {
    case PAY_FREQUENCY.weekly:
      return Math.min(diffWeeks, 52); // Limit to 52 weeks per year
    case PAY_FREQUENCY.monthly:
      return Math.min(diffMonths, 12); // Limit to 12 months per year
    case PAY_FREQUENCY.annually:
      return 1;
    default:
      return 0;
  }
}

export const getIncomeWithRange = (fieldId, records) => {
  if (!records || !records?.length) {
    return {
      [fieldId]: [],
      totalIncome: 0,
    };
  }

  // Get unique memberIds
  const memberIds = getUniqueMemberIds(records);

  // Process records for each member
  const memberIncomes = memberIds.map((memberId) => {
    const memberRecords = getMemberRecords(records, memberId);

    const incomeItems = [];

    memberRecords.forEach((record) => {
      const {
        payFrequency,
        startDate,
        endDate,
        weeklyAmount,
        monthlyAmount,
        annualAmount,
        source,
      } = record;

      // Get base pay rate from appropriate field
      const basePayRate = getBasePayRate({
        payFrequency,
        weeklyAmount,
        monthlyAmount,
        annualAmount,
      });

      // Skip if no amount
      if (!basePayRate) {
        return;
      }

      const periods = calculatePeriods(startDate, endDate, payFrequency);
      let totalIncome = 0;

      switch (payFrequency) {
        case PAY_FREQUENCY.weekly:
          totalIncome = basePayRate * periods;
          break;
        case PAY_FREQUENCY.monthly:
          totalIncome = basePayRate * periods;
          break;
        case PAY_FREQUENCY.annually:
          totalIncome = basePayRate;
          break;
        default:
          return;
      }

      incomeItems.push(
        getIncomeItem({
          memberId,
          payType: source || INCOME_CHECKLIST_PAY_TYPES.income,
          startDate,
          endDate,
          payFrequency,
          payRate: basePayRate,
        }),
      );
    });

    // Calculate member's total income
    const memberTotalIncome = getTotalByFieldName(incomeItems, 'incomeTotal');

    return {
      memberId,
      [fieldId]: incomeItems,
      totalIncome: memberTotalIncome,
    };
  });

  return getGrandTotal({
    items: memberIncomes,
    fieldId,
  });
};
